import app from 'flarum/forum/app';
import { extend, override } from 'flarum/common/extend';
import ComposerState from 'flarum/forum/states/ComposerState';
// Type only: Composer itself is a lazy chunk, patched through its module path below.

let proxy = null;

/**
 * On a tablet or phone, opening the composer ("Répondre", "Modifier",
 * "Nouvelle discussion") puts the caret - and the keyboard - straight in it,
 * instead of needing a second tap in the text area (reported 2026-10-12 from
 * an iPad).
 *
 * iOS only lets a script focus a field, caret and keyboard included, from
 * within the tap itself. Core focuses the composer well after it: the reply
 * action first awaits load() (ReplyComposer is a lazy chunk), then show()
 * (Composer too, the first time), and Composer.focus() only runs once the
 * opening animation ends. So the tap itself focuses an invisible field - from
 * the synchronous start of load(), or of show() when the composer already
 * holds that reply: whichever the action calls first, still inside the tap -
 * the keyboard opens for it, and focus moves on to the real field once the
 * composer is up: iOS allows that hand-over while a field has the keyboard.
 *
 * Composer.focus() also aims at the editor itself for a reply or an edit:
 * core's fallback picks the first `:input` in the composer, buttons
 * included - fof/polls' "Ajouter un sondage" when discussion polls are on,
 * which then took the focus even on a computer. A new discussion keeps its
 * own target, the title (DiscussionComposer.focusOnSelector).
 */
export default function focusComposerOnTouch() {
  extend(ComposerState.prototype, ['load', 'show'], function () {
    const opening = this.position !== ComposerState.Position.NORMAL && this.position !== ComposerState.Position.FULLSCREEN;
    if (opening && window.matchMedia('(any-pointer: coarse)').matches) holdKeyboard();
  });
  override('flarum/forum/components/Composer', 'focus', function (original) {
    const body = app.composer.body;
    if (!body.componentClass?.focusOnSelector && app.composer.editor) {
      app.composer.editor.focus();
    } else {
      original();
    }
    releaseKeyboard();
  });

  // Composer closed before it ever got the focus: don't leave the field behind.
  extend(ComposerState.prototype, 'hide', releaseKeyboard);
}
function holdKeyboard() {
  // Already held since the tap (load() then show()): a new field focused
  // now, outside the tap, would only close the keyboard.
  if (proxy) return;
  proxy = document.createElement('input');
  proxy.type = 'text';
  proxy.setAttribute('aria-hidden', 'true');
  proxy.tabIndex = -1;
  // In view (iOS scrolls to an off-screen field) but invisible; 16px so
  // iOS doesn't zoom in on it.
  proxy.style.cssText = 'position:fixed;bottom:0;left:0;width:1px;height:1px;opacity:0;border:0;padding:0;font-size:16px;';
  document.body.appendChild(proxy);
  proxy.focus();

  // Should the composer never take the focus, the field goes anyway.
  const held = proxy;
  setTimeout(() => {
    if (proxy === held) releaseKeyboard();
  }, 3000);
}
function releaseKeyboard() {
  proxy?.remove();
  proxy = null;
}